import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { ASSETS } from "../data";

function formatErr(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (user) nav("/admin", { replace: true }); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/admin", { replace: true });
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] noise-overlay flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-md border border-white/10 bg-[#0a0a0a] p-10"
      >
        <div className="flex items-center gap-3 mb-8">
          <img src={ASSETS.logo} alt="Ghost The Grid" className="h-10 w-10 object-cover rounded-sm" />
          <span className="font-display font-extrabold uppercase tracking-tight">Admin Access</span>
        </div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/40 mb-8 flex items-center gap-2">
          <Lock size={13} /> Restricted // Authorized personnel only
        </p>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/50 block mb-2">Email</label>
            <input
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full bg-black/40 border border-white/15 px-4 py-3 font-mono text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-accent transition-colors"
              placeholder="you@ghostthegrid.com"
            />
          </div>
          <div>
            <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/50 block mb-2">Password</label>
            <input
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full bg-black/40 border border-white/15 px-4 py-3 font-mono text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-accent transition-colors"
              placeholder="••••••••"
            />
          </div>
          {error && <p data-testid="login-error" className="font-mono text-xs text-accent">{error}</p>}
          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-accent text-white font-mono uppercase tracking-[0.15em] text-sm px-6 py-4 hover:bg-white hover:text-black transition-colors duration-300 disabled:opacity-70"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : "Enter Dashboard"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
